/**
 * autolink — render text with linkified URLs and @usernames.
 *
 * URLs (`http://`, `https://`) become `<a href target=_blank>`. Bare
 * `@Username` becomes a link to `/profile/<u>/`. Both are safe: we never
 * dangerouslySetInnerHTML — the splitter returns a ReactNode[] of plain
 * strings + anchor elements so React handles escaping.
 */
import type { ReactNode } from 'react';

const URL_RE = /(https?:\/\/[^\s<>"')]+)|(@[A-Za-z0-9_][A-Za-z0-9_ \-]{1,30}[A-Za-z0-9_])/g;

function trimUrlTrailingPunct(u: string): { url: string; tail: string } {
    // Strip a trailing ),. ?, ! etc. so `(see https://x.com)` doesn't
    // include the closing paren in the link.
    const m = /^(.*?)([)\].,!?;]+)$/.exec(u);
    if (!m) return { url: u, tail: '' };
    return { url: m[1] ?? u, tail: m[2] ?? '' };
}

export function autolink(text: string): ReactNode[] {
    if (!text) return [];
    const out: ReactNode[] = [];
    let last = 0;
    URL_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    let key = 0;
    while ((m = URL_RE.exec(text)) !== null) {
        if (m.index > last) out.push(text.slice(last, m.index));
        if (m[1]) {
            const { url, tail } = trimUrlTrailingPunct(m[1]);
            out.push(
                <a key={`u${key++}`} href={url} target="_blank" rel="noopener noreferrer" className="msg-link">{url}</a>
            );
            if (tail) out.push(tail);
        } else if (m[2]) {
            const tok = m[2];                 // includes leading '@'
            const name = tok.slice(1).trim();
            // single-word names link directly; multi-word "@First Last"
            // matches the friendly form some folks use, still safe.
            const href = `/profile/${encodeURIComponent(name)}/`;
            out.push(
                <a key={`m${key++}`} href={href} target="_blank" rel="noopener" className="msg-mention-link">{tok}</a>
            );
        }
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}
